// src/components/AlertBanner.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

// Environment-based configuration
const BANNER_ENABLED = process.env.REACT_APP_ALERT_BANNER_ENABLED === 'true';
const BANNER_MESSAGE = process.env.REACT_APP_ALERT_BANNER_MESSAGE || "I'm currently making changes to this site. Things may periodically be unstable.";
const BANNER_TIMESTAMP = process.env.REACT_APP_ALERT_BANNER_TIMESTAMP || '2025-01-08 10:45 AM MT';
const BANNER_VERSION = process.env.REACT_APP_ALERT_BANNER_VERSION || 'v1'; // Increment to show banner again after dismissal

const BannerWrapper = styled.div`
  background: #fef3c7;
  border-bottom: 1px solid #fcd34d;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.base};
    gap: ${({ theme }) => theme.spacing.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.base};
    gap: 0;
  }
`;

const BannerSpacer = styled.div`
  height: ${({ $height }) => $height}px;
`;

const BannerText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  line-height: ${({ theme }) => theme.lineHeights.normal};

  strong {
    color: #b45309;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }
`;

const DismissLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #b45309;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-decoration: underline;
  transition: color ${({ theme }) => theme.transitions.fast};
  /* Ensure minimum 44px touch target for accessibility */
  min-height: 44px;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    color: #92400e;
  }

  &:active {
    color: #78350f;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const AlertBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerRef = useRef(null);

  useEffect(() => {
    // Only show if enabled via environment variable
    if (!BANNER_ENABLED) return;

    // Check if banner was dismissed for this version
    const dismissedVersion = localStorage.getItem('alertBannerDismissed');
    if (dismissedVersion !== BANNER_VERSION) {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    if (showBanner && bannerRef.current) {
      const height = bannerRef.current.offsetHeight;
      setBannerHeight(height);
      document.documentElement.style.setProperty('--alert-banner-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--alert-banner-height', '0px');
    }
  }, [showBanner]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('alertBannerDismissed', BANNER_VERSION);
    document.documentElement.style.setProperty('--alert-banner-height', '0px');
  };

  if (!showBanner) return null;

  return (
    <>
      <BannerWrapper ref={bannerRef}>
        <BannerText>
          <strong>Heads up:</strong> {BANNER_MESSAGE}{BANNER_TIMESTAMP && <em> Updated {BANNER_TIMESTAMP}.</em>}
        </BannerText>
        <DismissLink onClick={dismissBanner}>Dismiss warning</DismissLink>
      </BannerWrapper>
      <BannerSpacer $height={bannerHeight} />
    </>
  );
};

export default AlertBanner;
